import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type {
  RoomSectionCameraDir,
  RoomSectionCenterCoordinate,
  RoomSectionDirection,
  RoomSectionRenderItem,
} from "./types";

const disposeSceneResources = (scene: THREE.Scene) => {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();

  scene.traverse((object) => {
    const maybeGeometry = (object as { geometry?: THREE.BufferGeometry })
      .geometry;
    if (maybeGeometry) {
      geometries.add(maybeGeometry);
    }

    const maybeMaterial = (
      object as { material?: THREE.Material | THREE.Material[] }
    ).material;
    if (Array.isArray(maybeMaterial)) {
      maybeMaterial.forEach((material) => materials.add(material));
    } else if (maybeMaterial) {
      materials.add(maybeMaterial);
    }
  });

  geometries.forEach((geometry) => geometry.dispose());
  materials.forEach((material) => material.dispose());
};

export type RoomSectionIsoViewerProps = {
  cameraDir?: RoomSectionCameraDir;
  renderItems: RoomSectionRenderItem[];
  sectionCenterCoordinate: RoomSectionCenterCoordinate | null;
  selectedSection: RoomSectionDirection;
  onItemClick?: (item: RoomSectionRenderItem["item"]) => void;
};

export const RoomSectionIsoViewer: React.FC<RoomSectionIsoViewerProps> = ({
  cameraDir = { x: 1, y: 1, z: 1 },
  renderItems,
  sectionCenterCoordinate,
  selectedSection,
  onItemClick,
}) => {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    const w = container.clientWidth || 400;
    const h = container.clientHeight || 300;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf8f9fa);

    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.0);
    dirLight.position.set(1, 1, 3);
    scene.add(dirLight);

    const renderObbs = renderItems.flatMap(
      ({ item, color, opacity, outline }) =>
        (item.geometry?.Obb3DWorldMmList ?? []).map((obb) => ({
          item,
          obb,
          color,
          opacity,
          outline,
        })),
    );
    const obbs = renderObbs.map(({ obb }) => obb);
    if (obbs.length === 0) return;

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;

    for (const obb of obbs) {
      const { x, y, z } = obb.centerMm;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
      minZ = Math.min(minZ, z);
      maxZ = Math.max(maxZ, z);
    }

    const center = new THREE.Vector3(
      (minX + maxX) / 2,
      (minY + maxY) / 2,
      (minZ + maxZ) / 2,
    );
    const sceneSize = Math.max(maxX - minX, maxY - minY, maxZ - minZ, 1);

    const camDirVec = new THREE.Vector3(
      cameraDir.x,
      cameraDir.y,
      cameraDir.z,
    ).normalize();
    const useClipping =
      selectedSection !== "isometric" && sectionCenterCoordinate != null;

    let clipPlane: THREE.Plane | null = null;
    if (useClipping && sectionCenterCoordinate) {
      const [wx, wy] = sectionCenterCoordinate;
      const widgetCenter = new THREE.Vector3(wx, wy, 0);
      const planeNormal = camDirVec.clone().negate();
      clipPlane = new THREE.Plane(planeNormal, camDirVec.dot(widgetCenter));
    }

    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      stencil: useClipping,
    });
    renderer.setSize(w, h);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.localClippingEnabled = useClipping;
    container.appendChild(renderer.domElement);

    const visualMeshes: THREE.Mesh[] = [];

    for (const { item, obb, color, opacity, outline } of renderObbs) {
      const {
        centerMm: c,
        axisA,
        axisB,
        axisC,
        extentAMm: eA,
        extentBMm: eB,
        extentCMm: eC,
      } = obb;

      const geometry = new THREE.BoxGeometry(
        (2 * eA) / 1000,
        (2 * eB) / 1000,
        (2 * eC) / 1000,
      );

      const material = new THREE.MeshPhongMaterial({
        color: new THREE.Color(color),
        specular: new THREE.Color(0x224466),
        shininess: 40,
        opacity,
        transparent: opacity < 1,
        clippingPlanes: clipPlane ? [clipPlane] : [],
      });

      const mesh = new THREE.Mesh(geometry, material);
      mesh.userData.item = item;
      mesh.position.set(c.x, c.y, c.z);
      mesh.quaternion.setFromRotationMatrix(
        new THREE.Matrix4().set(
          axisA.x,
          axisB.x,
          axisC.x,
          0,
          axisA.y,
          axisB.y,
          axisC.y,
          0,
          axisA.z,
          axisB.z,
          axisC.z,
          0,
          0,
          0,
          0,
          1,
        ),
      );
      mesh.renderOrder = 2;
      scene.add(mesh);
      visualMeshes.push(mesh);

      if (outline) {
        const edges = new THREE.EdgesGeometry(geometry);
        const lineMaterial = new THREE.LineBasicMaterial({
          color: new THREE.Color(outline.color),
          opacity: outline.opacity ?? 1,
          transparent: (outline.opacity ?? 1) < 1,
          linewidth: outline.width ?? 1,
          depthTest: outline.renderOnTop === false,
          depthWrite: outline.renderOnTop === false,
          clippingPlanes: clipPlane ? [clipPlane] : [],
        });
        const lineSegments = new THREE.LineSegments(edges, lineMaterial);
        lineSegments.position.copy(mesh.position);
        lineSegments.quaternion.copy(mesh.quaternion);
        lineSegments.renderOrder = outline.renderOnTop === false ? 2 : 5;
        scene.add(lineSegments);
      }
    }

    if (clipPlane && useClipping) {
      for (const visMesh of visualMeshes) {
        const backMat = new THREE.MeshBasicMaterial({
          side: THREE.BackSide,
          depthWrite: false,
          depthTest: false,
          colorWrite: false,
          stencilWrite: true,
          stencilFunc: THREE.AlwaysStencilFunc,
          stencilFail: THREE.IncrementWrapStencilOp,
          stencilZFail: THREE.IncrementWrapStencilOp,
          stencilZPass: THREE.IncrementWrapStencilOp,
        });
        const backMesh = new THREE.Mesh(visMesh.geometry, backMat);
        backMesh.position.copy(visMesh.position);
        backMesh.quaternion.copy(visMesh.quaternion);
        backMesh.renderOrder = 1;
        scene.add(backMesh);

        const frontMat = new THREE.MeshBasicMaterial({
          side: THREE.FrontSide,
          depthWrite: false,
          depthTest: false,
          colorWrite: false,
          stencilWrite: true,
          stencilFunc: THREE.AlwaysStencilFunc,
          stencilFail: THREE.DecrementWrapStencilOp,
          stencilZFail: THREE.DecrementWrapStencilOp,
          stencilZPass: THREE.DecrementWrapStencilOp,
        });
        const frontMesh = new THREE.Mesh(visMesh.geometry, frontMat);
        frontMesh.position.copy(visMesh.position);
        frontMesh.quaternion.copy(visMesh.quaternion);
        frontMesh.renderOrder = 1;
        scene.add(frontMesh);
      }

      const capGeometry = new THREE.PlaneGeometry(
        sceneSize * 10,
        sceneSize * 10,
      );
      const capMaterial = new THREE.MeshBasicMaterial({
        color: 0x9ec5e8,
        side: THREE.DoubleSide,
        stencilWrite: true,
        stencilRef: 0,
        stencilFunc: THREE.NotEqualStencilFunc,
        stencilFail: THREE.ReplaceStencilOp,
        stencilZFail: THREE.ReplaceStencilOp,
        stencilZPass: THREE.ReplaceStencilOp,
      });
      const capMesh = new THREE.Mesh(capGeometry, capMaterial);
      capMesh.renderOrder = 3;

      const [wx, wy] = sectionCenterCoordinate!;
      capMesh.position.set(wx, wy, center.z);
      capMesh.quaternion.setFromUnitVectors(
        new THREE.Vector3(0, 0, 1),
        camDirVec,
      );
      scene.add(capMesh);
    }

    const aspect = w / h;
    const viewSize = sceneSize * 0.8;
    const camera = new THREE.OrthographicCamera(
      (-viewSize * aspect) / 2,
      (viewSize * aspect) / 2,
      viewSize / 2,
      -viewSize / 2,
      0.1,
      sceneSize * 20,
    );

    camera.position.copy(center).addScaledVector(camDirVec, sceneSize * 5);
    camera.up.set(0, 0, 1);
    camera.lookAt(center);

    const render = () => {
      renderer.render(scene, camera);
    };

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.copy(center);
    controls.enableDamping = false;
    controls.enableRotate = false;
    controls.enablePan = true;
    controls.enableZoom = true;
    controls.screenSpacePanning = true;
    controls.zoomSpeed = 1.8;
    controls.panSpeed = 0.9;
    controls.minZoom = 0.2;
    controls.maxZoom = 20;
    controls.mouseButtons = {
      LEFT: null,
      MIDDLE: null,
      RIGHT: null,
    };
    controls.touches = {
      ONE: THREE.TOUCH.PAN,
      TWO: THREE.TOUCH.DOLLY_PAN,
    };
    controls.addEventListener("change", render);
    controls.update();

    render();

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let middlePanStartPosition: { x: number; y: number } | null = null;

    const handlePointerDown = (event: PointerEvent) => {
      if (event.button !== 1) return;

      middlePanStartPosition = { x: event.clientX, y: event.clientY };
      event.preventDefault();
      renderer.domElement.style.cursor = "grabbing";
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (!middlePanStartPosition || event.buttons !== 4) return;

      const rect = renderer.domElement.getBoundingClientRect();
      const dx = event.clientX - middlePanStartPosition.x;
      const dy = event.clientY - middlePanStartPosition.y;
      middlePanStartPosition = { x: event.clientX, y: event.clientY };

      const worldPerPixelX = (camera.right - camera.left) / camera.zoom / rect.width;
      const worldPerPixelY = (camera.top - camera.bottom) / camera.zoom / rect.height;
      const panOffset = new THREE.Vector3();
      const cameraRight = new THREE.Vector3();
      const cameraUp = new THREE.Vector3();

      camera.updateMatrixWorld();
      cameraRight.setFromMatrixColumn(camera.matrix, 0);
      cameraUp.setFromMatrixColumn(camera.matrix, 1);
      panOffset
        .addScaledVector(cameraRight, -dx * worldPerPixelX)
        .addScaledVector(cameraUp, dy * worldPerPixelY);

      camera.position.add(panOffset);
      controls.target.add(panOffset);
      controls.update();
      render();
    };

    const handlePointerUp = () => {
      middlePanStartPosition = null;
      renderer.domElement.style.cursor = "default";
    };

    const handleClick = (event: MouseEvent) => {
      if (event.button !== 0) return;
      if (!onItemClick) return;

      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);

      const hit = raycaster.intersectObjects(visualMeshes, false)[0];
      const item = hit?.object.userData.item as
        | RoomSectionRenderItem["item"]
        | undefined;
      if (item) {
        onItemClick(item);
      }
    };

    renderer.domElement.style.cursor = "default";
    renderer.domElement.style.touchAction = "none";
    renderer.domElement.addEventListener("pointerdown", handlePointerDown);
    renderer.domElement.addEventListener("pointermove", handlePointerMove);
    renderer.domElement.addEventListener("pointerup", handlePointerUp);
    renderer.domElement.addEventListener("pointerleave", handlePointerUp);
    renderer.domElement.addEventListener("click", handleClick);

    const resizeObserver = new ResizeObserver(() => {
      const newW = container.clientWidth;
      const newH = container.clientHeight;
      const newAspect = newW / newH;
      camera.left = (-viewSize * newAspect) / 2;
      camera.right = (viewSize * newAspect) / 2;
      camera.updateProjectionMatrix();
      renderer.setSize(newW, newH);
      render();
    });
    resizeObserver.observe(container);

    return () => {
      controls.removeEventListener("change", render);
      controls.dispose();
      renderer.domElement.removeEventListener("pointerdown", handlePointerDown);
      renderer.domElement.removeEventListener("pointermove", handlePointerMove);
      renderer.domElement.removeEventListener("pointerup", handlePointerUp);
      renderer.domElement.removeEventListener("pointerleave", handlePointerUp);
      renderer.domElement.removeEventListener("click", handleClick);
      resizeObserver.disconnect();
      disposeSceneResources(scene);
      renderer.renderLists.dispose();
      renderer.dispose();
      renderer.forceContextLoss();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [
    renderItems,
    cameraDir.x,
    cameraDir.y,
    cameraDir.z,
    sectionCenterCoordinate,
    selectedSection,
    onItemClick,
  ]);

  return <div ref={mountRef} style={{ width: "100%", height: "100%" }} />;
};
